<template>
    <pendo-multiselect
        :options="groupedOptions"
        :value="selectedValue"
        group-options-key="options"
        group-label-key="label"
        :multiple="false"
        :close-on-select="true"
        :max-menu-height="400"
        :placeholder="$t('askPendo.savedChat.Select an option')"
        :no-results-text="$t('askPendo.savedChat.No conversations found')"
        @select="handleSelect">
        <template #trigger="{ comboboxAttrs, handleKeydown, toggleMenu, isOpen, setTriggerRef }">
            <div
                :ref="setTriggerRef"
                v-bind="comboboxAttrs"
                class="saved-chat-trigger"
                :class="{ 'is-open': isOpen }"
                @click="toggleMenu"
                @keydown="handleKeydown">
                <pendo-icon
                    type="list"
                    size="16" />
                {{ $t('askPendo.savedChat.Previous chats') }}
                <pendo-icon
                    type="chevron-down"
                    size="12"
                    class="saved-chat-trigger__chevron"
                    :class="{ 'rotated': isOpen }" />
            </div>
        </template>
        <template #noData>
            <div class="no-data-message">
                {{ $t('askPendo.savedChat.No previous chats') }}
            </div>
        </template>

        <template #header="{ comboboxAttrs, handleKeydown, updateInputValue, inputValue }">
            <div class="custom-search-header">
                <search
                    v-bind="comboboxAttrs"
                    :value="inputValue"
                    autofocus
                    full-width
                    :placeholder="$t('askPendo.savedChat.Search...')"
                    @input="updateInputValue"
                    @keydown="handleKeydown" />
            </div>
        </template>
    </pendo-multiselect>
</template>

<script>
import { PendoMultiselect, PendoIcon, PendoNotification } from '@pendo/components';
import Search from '@/common/search/search.vue';
import { mapActions, mapGetters } from 'vuex';
import { formatConversationsForDropdown } from '@/ask-pendo/utils/conversation-grouping.utils';
import { EventBus } from '@utils/event-bus';

export default {
    name: 'AskPendoSavedChat',
    components: {
        PendoMultiselect,
        PendoIcon,
        Search,
    },
    data () {
        return {
            selectedValue: null,
        };
    },
    computed: {
        ...mapGetters('askPendo', ['userConversations']),

        groupedOptions () {
            return formatConversationsForDropdown(this.userConversations);
        },
    },
    async mounted () {
        try {
            await this.loadAll();
            EventBus.$on('conversation:saved', this.handleConversationSaved);
        } catch {
            PendoNotification({
                type: 'error',
                title: this.$t('askPendo.savedChat.Unable to load previous chats'),
                message: this.$t('askPendo.savedChat.Please try again later'),
                duration: 4000,
            });
        }
    },
    beforeUnmount () {
        EventBus.$off('conversation:saved', this.handleConversationSaved);
    },
    methods: {
        ...mapActions('askPendo', ['loadAll']),

        handleConversationSaved () {
            this.loadAll({ reFetch: true });
        },

        handleSelect (option) {
            this.selectedValue = option;
            this.$router.push({
                name: 'AgentModeConversation',
                params: { id: option.id },
            });
        },
    },
};
</script>

<style scoped lang="less">
.saved-chat-trigger {
    color: var(--palette-gray-90);
    background-color: var(--palette-gray-0);
    border: 1px solid var(--palette-gray-40);
    padding: 8px;
    border-radius: 3px;
    height: 36px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    user-select: none;
    transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;

    &:hover, &.is-open {
        background-color: var(--palette-gray-10);
        border-color: var(--palette-gray-60);
        color: var(--palette-gray-90);
    }

    &:focus-visible {
        outline: 1px solid var(--palette-gray-90);
    }

    &__chevron {
        transition: transform 0.2s ease;
        margin-left: auto;

        &.rotated {
            transform: rotate(180deg);
        }
    }
}

.custom-search-header {
    padding: 12px 16px;
    border-bottom: 1px solid var(--palette-gray-40);
}
</style>
